import {
  ArrowForward,
  BlurCircularOutlined,
  Close,
  Fullscreen,
  Groups,
  NotificationsActive,
  ViewColumn,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  lighten,
  useTheme,
} from '@mui/material';
import {
  MRT_ColumnDef,
  MRT_ExpandedState,
  MRT_PaginationState,
  MRT_Row,
  MRT_SortingState,
  MRT_VisibilityState,
} from 'material-react-table';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';
import SwitchTable from 'switch-fe-shared/components/Tables/SwitchTable';
import { AlertState } from 'switch-shared/modules/crud/alert/alert.enum';
import { UtilityTypeOrAll } from 'switch-shared/modules/crud/utility/utility.enums';
import {
  ApplicationGroupOverview,
  MetricBreakdown,
} from 'switch-shared/modules/dashboard/dashboard.dto';

import { formatNumber } from '../../../../../../switch-shared/src/utils/formatters';
import { ACTIONS } from '../../../../auth/utils/defineAbilityFor';
import { AbilityContext } from '../../../../context/canContext';
import { useDashboard } from '../../../../context/dashboardContext';
import { useAdminUrlNavigation } from '../../../../hooks/useAdminUrlNavigation';
import { useNavigateWithState } from '../../../../hooks/useNavigateWithState';
import { useSelectedApplicationGroupId } from '../../../../hooks/useSelectedApplicationGroupId';
import { useSelectedApplicationId } from '../../../../hooks/useSelectedApplicationId';
import pages from '../../../../router/routes';
import { useAlertFindQuery } from '../../../../services/switch-rest-api/alerts.service';
import { isUtilityType } from '../../../../utils/selection';
import { useAllGroupsMetrics } from '../../hooks/useAllGroupsMetrics';
import AlertsModal from '../modals/AlertsModal';

// Extended type to support both group and application rows
type TableRow = {
  _id: string;
  name: string;
  type: 'group' | 'application';
  groupId: string;
  totalApplications?: number;
  meters: MetricBreakdown;
  gateways: MetricBreakdown;
  activeAlerts: number;
  assignedUsers?: number;
  subRows?: TableRow[];
};

const COLUMN_SORT_FIELD_MAP: Record<string, string> = {
  name: 'applicationGroupName',
  totalApplications: 'totalApplications',
  'meters.total': 'meters.total',
  'gateways.total': 'gateways.total',
  activeAlerts: 'activeAlerts',
  assignedUsers: 'assignedUsers',
};

interface MetricCellProps {
  metrics: MetricBreakdown;
  type: 'meters' | 'gateways';
  rowType: 'group' | 'application';
  groupId: string;
  applicationId?: string;
  utilityType?: UtilityTypeOrAll;
  onNavigate: (
    groupId: string,
    appId: string | undefined,
    path: string
  ) => void;
}

const MetricCell = ({
  metrics,
  type,
  rowType,
  groupId,
  applicationId,
  utilityType,
  onNavigate,
}: MetricCellProps) => {
  const theme = useTheme();
  const { getApplicationScopedPath } = useAdminUrlNavigation();
  const hasUnits = metrics.total > 0;
  const percentageColor =
    metrics.onlinePercentage >= 90
      ? theme.palette.success.main
      : metrics.onlinePercentage >= 70
        ? theme.palette.warning.main
        : theme.palette.error.main;

  const getFilteredPath = (isOnline: boolean) => {
    const sectionPath = type === 'meters' ? 'meters' : 'gateways';
    const legacyPath =
      type === 'meters' ? pages.meters.path : pages.gateways.path;
    const path = getApplicationScopedPath(
      sectionPath,
      legacyPath,
      groupId,
      applicationId
    );
    const params = new URLSearchParams();
    const filters = [
      ...(type === 'meters' && utilityType && isUtilityType(utilityType)
        ? [`utilityType:${utilityType}`]
        : []),
      `isOnline:${isOnline}`,
    ];
    params.set('filters', filters.join(','));
    return `${path}?${params.toString()}`;
  };

  return (
    <Stack direction='row' alignItems='center' spacing={1}>
      <Typography variant='body2'>{formatNumber(metrics.total, 0)}</Typography>
      {hasUnits && (
        <Tooltip
          slotProps={{
            tooltip: {
              sx: {
                background: 'white',
                color: theme.tokens.colors.cardForeground,
                boxShadow: theme.tokens.shadows.lg,
                borderRadius: theme.tokens.borderRadius.md,
                padding: 0,
                minWidth: 175,
              },
            },
          }}
          title={
            <Box>
              <Stack
                spacing={1}
                sx={{
                  px: `${theme.tokens.spacing.md}px`,
                  py: `${theme.tokens.spacing.sm}px`,
                }}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  spacing={2}
                >
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: theme.tokens.borderRadius.full,
                        backgroundColor: theme.palette.success.main,
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Online
                    </Typography>
                  </Stack>
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: theme.tokens.typography.fontWeight.semibold,
                      }}
                    >
                      {formatNumber(metrics.online, 0)}
                    </Typography>
                    {rowType === 'application' && (
                      <Button
                        size='small'
                        variant='text'
                        onClick={(event) => {
                          event.stopPropagation();
                          onNavigate(
                            groupId,
                            applicationId,
                            getFilteredPath(true)
                          );
                        }}
                        sx={{
                          minWidth: 'auto',
                          px: 1,
                          py: '1px',
                        }}
                        aria-label='View'
                      >
                        <ArrowForward fontSize='small' />
                      </Button>
                    )}
                  </Stack>
                </Stack>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  spacing={2}
                >
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: theme.tokens.borderRadius.full,
                        backgroundColor: theme.palette.error.main,
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{
                        fontSize: '0.875rem',
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Offline
                    </Typography>
                  </Stack>
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: theme.tokens.typography.fontWeight.semibold,
                      }}
                    >
                      {formatNumber(metrics.offline, 0)}
                    </Typography>
                    {rowType === 'application' && (
                      <Button
                        size='small'
                        variant='text'
                        onClick={(event) => {
                          event.stopPropagation();
                          onNavigate(
                            groupId,
                            applicationId,
                            getFilteredPath(false)
                          );
                        }}
                        sx={{
                          minWidth: 'auto',
                          px: 1,
                          py: '1px',
                        }}
                        aria-label='View'
                      >
                        <ArrowForward fontSize='small' />
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          }
        >
          <Chip
            size='small'
            label={`${metrics.onlinePercentage}%`}
            sx={{
              backgroundColor: percentageColor,
              color: theme.palette.common.white,
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 20,
              cursor: 'default',
            }}
          />
        </Tooltip>
      )}
    </Stack>
  );
};

interface AlertCellProps {
  count: number;
  rowType: 'group' | 'application';
  groupId: string;
  applicationId?: string;
  entityName: string;
  onViewAlerts: (
    groupId: string,
    appId: string | undefined,
    entityName: string
  ) => void;
}

const AlertCell = ({
  count,
  rowType,
  groupId,
  applicationId,
  entityName,
  onViewAlerts,
}: AlertCellProps) => {
  const theme = useTheme();
  const hasAlerts = count > 0;

  const handleClick = () => {
    onViewAlerts(groupId, applicationId, entityName);
  };

  const tooltipContent = hasAlerts ? (
    <Box>
      <Stack
        spacing={1}
        sx={{
          px: `${theme.tokens.spacing.md}px`,
          py: `${theme.tokens.spacing.sm}px`,
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          spacing={2}
        >
          <Stack direction='row' alignItems='center' spacing={1}>
            <NotificationsActive
              sx={{
                fontSize: '1rem',
                color: theme.palette.error.main,
              }}
            />
            <Typography
              variant='body2'
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Active Alert{count !== 1 ? 's' : ''}
            </Typography>
          </Stack>
          <Typography
            variant='body2'
            sx={{
              fontWeight: theme.tokens.typography.fontWeight.semibold,
            }}
          >
            {formatNumber(count, 0)}
          </Typography>
        </Stack>
      </Stack>
      <Box
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          px: `${theme.tokens.spacing.sm}px`,
          py: `${theme.tokens.spacing.sm}px`,
        }}
      >
        <Button
          size='small'
          variant='contained'
          endIcon={<ArrowForward />}
          onClick={handleClick}
          fullWidth
          sx={{ fontSize: theme.tokens.typography.fontSize.xs }}
        >
          View Alerts
        </Button>
      </Box>
    </Box>
  ) : (
    <Box
      sx={{
        px: `${theme.tokens.spacing.md}px`,
        py: `${theme.tokens.spacing.sm}px`,
      }}
    >
      <Stack direction='row' alignItems='center' spacing={1}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: theme.tokens.borderRadius.full,
            backgroundColor: theme.palette.success.main,
          }}
        />
        <Typography
          variant='body2'
          sx={{
            color: theme.palette.text.secondary,
          }}
        >
          No active alerts
        </Typography>
      </Stack>
    </Box>
  );

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            background: 'white',
            color: theme.tokens.colors.cardForeground,
            boxShadow: theme.tokens.shadows.lg,
            borderRadius: theme.tokens.borderRadius.md,
            padding: 0,
            minWidth: 175,
          },
        },
      }}
      title={tooltipContent}
    >
      <Stack direction='row' alignItems='center' spacing={0.5}>
        {hasAlerts && (
          <NotificationsActive
            fontSize='small'
            sx={{ color: theme.palette.error.main }}
          />
        )}
        <Typography
          variant='body2'
          sx={{
            color: hasAlerts ? theme.palette.error.main : 'inherit',
            fontWeight: hasAlerts ? 600 : 400,
            cursor: hasAlerts ? 'pointer' : 'default',
          }}
        >
          {formatNumber(count, 0)}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

const ApplicationGroupOverviewWidget = () => {
  /* Hooks & Use Effects */
  const theme = useTheme();
  const ability = useContext(AbilityContext);
  const { setSelectedGroupId: setSelectedApplicationGroupId } =
    useSelectedApplicationGroupId();
  const { setSelectedApplicationId: setSelectedApplicationIdFromHook } =
    useSelectedApplicationId();
  const { navigateToApplicationDashboard, navigateToApplicationGroup } =
    useAdminUrlNavigation();
  const { navigateWithApplicationContext } = useNavigateWithState();
  const { utility } = useDashboard();

  /* States */
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    {}
  );
  const [expanded, setExpanded] = useState<MRT_ExpandedState>({});
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const FULLSCREEN_PAGE_SIZE = 100;
  const [alertsModalOpen, setAlertsModalOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedAppId, setSelectedAppId] = useState<string | undefined>(
    undefined
  );
  const [selectedEntityName, setSelectedEntityName] = useState<string | null>(
    null
  );
  const [fullscreenPagination, setFullscreenPagination] =
    useState<MRT_PaginationState>({
      pageIndex: 0,
      pageSize: FULLSCREEN_PAGE_SIZE,
    });

  const columnMenuOpen = Boolean(columnMenuAnchorEl);
  const sortingParams = useMemo<{
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }>(() => {
    if (!sorting.length) {
      return { sortBy: undefined, sortDirection: undefined };
    }

    const [primarySort] = sorting;
    const mappedField =
      COLUMN_SORT_FIELD_MAP[
        primarySort.id as keyof typeof COLUMN_SORT_FIELD_MAP
      ];

    if (!mappedField) {
      return { sortBy: undefined, sortDirection: undefined };
    }

    return {
      sortBy: mappedField,
      sortDirection: primarySort.desc ? 'desc' : 'asc',
    };
  }, [sorting]);

  /* API Calls */
  const canReadGroups = ability.can(ACTIONS.READ, 'ApplicationGroups');
  const canReadApplications = ability.can(ACTIONS.READ, 'Applications');
  const canReadAlerts = ability.can(ACTIONS.READ, 'Alerts');

  // Use shared hook which fetches all groups with limit 100
  const {
    data: groupsData,
    isError: isGroupsError,
    isLoading: isGroupsLoading,
    isFetching: isGroupsFetching,
  } = useAllGroupsMetrics({
    sortBy: sortingParams.sortBy,
    sortDirection: sortingParams.sortDirection,
  });

  const {
    data: alertsData,
    isLoading: isAlertsLoading,
    isFetching: isAlertsFetching,
    isError: isAlertsError,
  } = useAlertFindQuery(
    {
      applicationGroupId: selectedGroupId || undefined,
      applicationId: selectedAppId,
      state: AlertState.Active,
      limit: '0',
    },
    {
      skip: !alertsModalOpen || !canReadAlerts,
    }
  );

  const isLoading = isGroupsLoading;
  const isFetching = isGroupsFetching;
  const isError = isGroupsError;

  /* Functions & Functional Components */
  const handleNavigateToGroup = useCallback(
    (groupId: string) => {
      setSelectedApplicationGroupId(groupId);
      navigateToApplicationGroup(groupId);
    },
    [navigateToApplicationGroup, setSelectedApplicationGroupId]
  );

  const handleNavigateToApplication = useCallback(
    (groupId: string, appId: string) => {
      setSelectedApplicationGroupId(groupId);
      setSelectedApplicationIdFromHook(appId);
      navigateToApplicationDashboard(appId, groupId);
    },
    [
      navigateToApplicationDashboard,
      setSelectedApplicationGroupId,
      setSelectedApplicationIdFromHook,
    ]
  );

  const handleNavigateToMetersOrGateways = useCallback(
    (groupId: string, appId: string | undefined, path: string) => {
      if (appId) {
        navigateWithApplicationContext(groupId, appId, path);
      }
    },
    [navigateWithApplicationContext]
  );

  const handleViewAlerts = useCallback(
    (groupId: string, appId: string | undefined, entityName: string) => {
      setSelectedGroupId(groupId);
      setSelectedAppId(appId);
      setSelectedEntityName(entityName);
      setAlertsModalOpen(true);
    },
    []
  );

  const handleCloseAlertsModal = useCallback(() => {
    setAlertsModalOpen(false);
    setSelectedGroupId(null);
    setSelectedAppId(undefined);
    setSelectedEntityName(null);
  }, []);

  const modalAlerts = useMemo(() => {
    return alertsData?.data || [];
  }, [alertsData]);

  const handleColumnMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setColumnMenuAnchorEl(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchorEl(null);
  };

  const handleColumnToggle = (columnId: string) => {
    setColumnVisibility((prev) => {
      const currentValue = prev[columnId as keyof typeof prev];
      const newValue = currentValue === false ? true : false;
      return {
        ...prev,
        [columnId]: newValue,
      };
    });
  };

  // Transform API data to table rows with subRows for applications
  const tableData: TableRow[] = useMemo(() => {
    const groups = (groupsData?.data ?? []) as ApplicationGroupOverview[];
    if (!groups.length) return [];

    const baseMetric: MetricBreakdown = {
      total: 0,
      online: 0,
      offline: 0,
      onlinePercentage: 0,
      offlinePercentage: 0,
    };

    return groups.map((group) => {
      const groupId = group.applicationGroupId;
      return {
        _id: groupId,
        name: group.applicationGroupName,
        type: 'group' as const,
        groupId,
        totalApplications: group.totalApplications,
        meters: group.meters ?? baseMetric,
        gateways: group.gateways ?? baseMetric,
        activeAlerts: group.activeAlerts ?? 0,
        assignedUsers: group.assignedUsers,
        subRows:
          group.applications && group.applications.length > 0
            ? group.applications.map((app) => ({
                _id: app.applicationId,
                name: app.applicationName,
                type: 'application' as const,
                groupId,
                meters: app.meters ?? baseMetric,
                gateways: app.gateways ?? baseMetric,
                activeAlerts: app.activeAlerts ?? 0,
                assignedUsers: app.assignedUsers,
              }))
            : undefined,
      };
    });
  }, [groupsData]);

  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    );
    setFullscreenPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    );
  }, [sorting]);

  useEffect(() => {
    if (isFullscreen) {
      setFullscreenPagination((prev) => ({
        ...prev,
        pageIndex: pagination.pageIndex,
      }));
    }
  }, [isFullscreen, pagination.pageIndex]);

  useEffect(() => {
    if (!tableData.length) {
      setExpanded({});
      return;
    }

    const expandedRows = tableData.reduce<Record<string, boolean>>(
      (acc, row) => {
        if (row.subRows && row.subRows.length > 0) {
          acc[row._id] = false;
        }
        return acc;
      },
      {}
    );
    setExpanded(expandedRows);
  }, [tableData]);

  const columns = useMemo<MRT_ColumnDef<TableRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 320,
        minSize: 320,
        maxSize: 320,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const isGroup = row.original.type === 'group';

          const handleNavigateClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (isGroup) {
              handleNavigateToGroup(row.original._id);
            } else {
              handleNavigateToApplication(
                row.original.groupId,
                row.original._id
              );
            }
          };

          const handleNameClick = (e: React.MouseEvent) => {
            if (isGroup) {
              e.stopPropagation();
              row.toggleExpanded();
            }
          };

          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pl: isGroup ? 0 : 2,
              }}
            >
              <Typography
                variant='body2'
                onClick={handleNameClick}
                sx={{
                  fontWeight: isGroup ? 600 : 400,
                  color: isGroup
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                  cursor: isGroup ? 'pointer' : 'default',
                }}
              >
                {row.original.name}
              </Typography>
              <Tooltip
                slotProps={{ tooltip: { sx: { mt: '5px !important' } } }}
                title={isGroup ? 'Select Group' : 'Select Application'}
              >
                <IconButton
                  size='small'
                  onClick={handleNavigateClick}
                  sx={{
                    my: -1,
                    color: theme.palette.primary.main,
                    opacity: 0,
                    pointerEvents: 'none',
                    transition: 'opacity 150ms ease',
                    '.MuiTableRow-root:hover &': {
                      opacity: 1,
                      pointerEvents: 'auto',
                    },
                  }}
                >
                  <ArrowForward sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        accessorKey: 'totalApplications',
        header: 'Applications',
        enableColumnFilter: false,
        size: 120,
        Cell: ({ row }) => {
          if (row.original.type === 'application') return null;
          return (
            <Typography variant='body2'>
              {row.original.totalApplications}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'meters.total',
        header: 'Meters',
        enableColumnFilter: false,
        size: 140,
        Cell: ({ row }) => (
          <MetricCell
            metrics={row.original.meters}
            type='meters'
            rowType={row.original.type}
            groupId={row.original.groupId}
            applicationId={
              row.original.type === 'application' ? row.original._id : undefined
            }
            utilityType={utility}
            onNavigate={handleNavigateToMetersOrGateways}
          />
        ),
      },
      {
        accessorKey: 'gateways.total',
        header: 'Gateways',
        enableColumnFilter: false,
        size: 140,
        Cell: ({ row }) => (
          <MetricCell
            metrics={row.original.gateways}
            type='gateways'
            rowType={row.original.type}
            groupId={row.original.groupId}
            applicationId={
              row.original.type === 'application' ? row.original._id : undefined
            }
            onNavigate={handleNavigateToMetersOrGateways}
          />
        ),
      },
      {
        accessorKey: 'activeAlerts',
        header: 'Alerts',
        enableColumnFilter: false,
        size: 100,
        Cell: ({ row }) => (
          <AlertCell
            count={row.original.activeAlerts}
            rowType={row.original.type}
            groupId={row.original.groupId}
            applicationId={
              row.original.type === 'application' ? row.original._id : undefined
            }
            entityName={row.original.name}
            onViewAlerts={handleViewAlerts}
          />
        ),
      },
      {
        accessorKey: 'assignedUsers',
        header: 'Users',
        enableColumnFilter: false,
        size: 100,
        Cell: ({ row }) => {
          return (
            <Stack
              direction='row'
              alignItems='center'
              spacing={0.5}
              justifyContent='flex-start'
            >
              <Groups
                fontSize='small'
                sx={{ color: theme.palette.text.secondary }}
              />
              <Typography variant='body2'>
                {row.original.assignedUsers ?? 0}
              </Typography>
            </Stack>
          );
        },
      },
    ],
    [
      theme,
      handleNavigateToMetersOrGateways,
      handleViewAlerts,
      handleNavigateToGroup,
      handleNavigateToApplication,
    ]
  );

  const columnIds = columns.map((col) => ({
    id: col.accessorKey as string,
    header:
      typeof col.header === 'string' ? col.header : (col.accessorKey as string),
  }));

  const renderRowActions = (row: MRT_Row<TableRow>, closeMenu: () => void) => {
    const isGroup = row.original.type === 'group';

    return [
      <MenuItem
        key='view'
        onClick={() => {
          if (isGroup) {
            handleNavigateToGroup(row.original.groupId);
          } else {
            handleNavigateToApplication(row.original.groupId, row.original._id);
          }
          closeMenu();
        }}
        disabled={isGroup ? !canReadGroups : !canReadApplications}
      >
        <ListItemIcon>
          <ArrowForward color='primary' />
        </ListItemIcon>
        <ListItemText>
          {isGroup ? 'Select Group' : 'Select Application'}
        </ListItemText>
      </MenuItem>,
    ];
  };

  const actions = (
    <>
      <Tooltip
        key='columns-tooltip'
        placement='bottom'
        title='Show/Hide columns'
      >
        <IconButton
          key='columns'
          aria-controls={columnMenuOpen ? 'column-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={columnMenuOpen ? 'true' : undefined}
          onClick={handleColumnMenuClick}
          size='small'
        >
          <ViewColumn />
        </IconButton>
      </Tooltip>
      <Menu
        key='column-menu'
        id='column-menu'
        anchorEl={columnMenuAnchorEl}
        open={columnMenuOpen}
        onClose={handleColumnMenuClose}
        MenuListProps={{
          'aria-labelledby': 'column-button',
        }}
      >
        {columnIds.map((column) => (
          <MenuItem
            key={column.id}
            sx={{ height: theme.tokens.spacing.xl * 1.3 }}
            onClick={() => handleColumnToggle(column.id)}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    columnVisibility[
                      column.id as keyof typeof columnVisibility
                    ] !== false
                  }
                />
              }
              label={column.header}
              sx={{ pointerEvents: 'none', width: '100%' }}
            />
          </MenuItem>
        ))}
      </Menu>
      <Tooltip key='fullscreen-tooltip' title='Fullscreen'>
        <IconButton
          key='fullscreen'
          onClick={(e) => {
            e.stopPropagation();
            setIsFullscreen(true);
          }}
          size='small'
        >
          <Fullscreen />
        </IconButton>
      </Tooltip>
    </>
  );

  const tableComponent = (isFullscreenMode: boolean) => (
    <SwitchTable<TableRow>
      title='Application Groups'
      columns={columns}
      data={tableData}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      enableTopToolbar={false}
      enableExpanding={true}
      enablePagination={true}
      manualPagination={false}
      manualSorting={true}
      enableMultiSort={false}
      getSubRows={(row) => row.subRows}
      getRowCanExpand={(row) => !!row.original.subRows?.length}
      renderRowActionMenuItems={({ row, closeMenu }) =>
        renderRowActions(row, closeMenu)
      }
      muiTablePaperProps={{
        sx: { maxHeight: isFullscreenMode ? '100%' : 'auto' },
      }}
      muiPaginationProps={{
        rowsPerPageOptions: [5, 10, 15, 20, 50, 100],
      }}
      state={{
        sorting,
        pagination: isFullscreenMode ? fullscreenPagination : pagination,
        columnVisibility,
        expanded,
      }}
      initialState={{
        density: 'compact',
      }}
      onSortingChange={setSorting}
      onPaginationChange={
        isFullscreenMode ? setFullscreenPagination : setPagination
      }
      onColumnVisibilityChange={setColumnVisibility}
      onExpandedChange={setExpanded}
      displayColumnDefOptions={{
        'mrt-row-expand': {
          size: 50,
          muiTableBodyCellProps: ({ row }) => ({
            sx: {
              color:
                row.original.type === 'group'
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
            },
          }),
        },
      }}
    />
  );

  return (
    <>
      <WidgetCard
        title='Groups Overview'
        subheader='Application group metrics and statuses'
        avatar={
          <Box
            sx={{
              padding: `${theme.tokens.spacing.sm}px`,
              height: `${theme.tokens.spacing.sm * 5}px`,
              width: `${theme.tokens.spacing.sm * 5}px`,
              mx: `-${theme.tokens.spacing.sm}px`,
              borderRadius: theme.tokens.borderRadius.full,
              background: theme.tokens.colors.card,
              boxShadow: theme.tokens.shadows.sm,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.primary.main,
            }}
          >
            <BlurCircularOutlined />
          </Box>
        }
        action={actions}
        cardContentProps={{
          sx: {
            display: 'flex',
            flex: 1,
            height: '80%',
            width: '100%',
            flexDirection: 'column',
            background: theme.colors.neutral.grey[20],
            borderTop: `1px solid ${theme.tokens.extendedColors.borders.light}`,
            paddingBottom: '0px !important',
            px: 0,
          },
        }}
      >
        {tableComponent(false)}
      </WidgetCard>

      {/* Fullscreen Modal */}
      <Dialog
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        fullScreen
        PaperProps={{
          sx: {
            background: theme.colors.neutral.grey[20],
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: 2, md: 3 },
              py: { xs: 1.5, md: 2 },
              borderBottom: `1px solid ${theme.tokens.colors.border}`,
              background: lighten(theme.palette.primary.main, 0.95),
            }}
          >
            <Box>
              <Typography
                variant={'h1'}
                fontSize={{
                  xs: theme.tokens.typography.fontSize.lg,
                  md: theme.tokens.typography.fontSize.xl,
                }}
                fontFamily={theme.tokens.typography.fontFamily.display}
                color={theme.palette.primary.main}
                fontWeight={theme.tokens.typography.fontWeight.semibold}
              >
                Groups Overview
              </Typography>
              <Typography
                variant='body2'
                sx={{ color: theme.tokens.colors.mutedForeground }}
              >
                Application group metrics and statuses
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title='Show/Hide columns'>
                <IconButton onClick={handleColumnMenuClick}>
                  <ViewColumn />
                </IconButton>
              </Tooltip>
              <Tooltip title='Close fullscreen'>
                <IconButton onClick={() => setIsFullscreen(false)}>
                  <Close />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Table */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
            }}
          >
            {tableComponent(true)}
          </Box>
        </Box>
      </Dialog>

      <AlertsModal
        open={alertsModalOpen}
        onClose={handleCloseAlertsModal}
        alerts={modalAlerts}
        title={
          selectedEntityName
            ? `Active Alerts - ${selectedEntityName}`
            : 'Active Alerts'
        }
        isLoading={isAlertsLoading || isAlertsFetching}
        key={`${selectedGroupId}-${selectedAppId}`}
      />
    </>
  );
};

export default ApplicationGroupOverviewWidget;
